/**
 * Motion tokens — the single vocabulary for app-wide animation.
 *
 * Mirrors how the MUI theme centralizes color: every animation reads its
 * durations / easings / springs from here so motion stays consistent and
 * restrained. A procurement tool must feel fast and serious, so durations are
 * deliberately short (<= 0.4s) and springs are crisp (see spec §11).
 */

/**
 * When `false` (the default), animations IGNORE the OS
 * `prefers-reduced-motion` setting and always play (spec §9 — decoupled by
 * default). Flip to `true` to respect the user's reduced-motion preference
 * app-wide. This single boolean is the source of truth consumed by:
 *   - the `<MotionConfig reducedMotion=…>` shell in `App.tsx`,
 *   - the `usePrefersReduced()` hook used by every motion primitive,
 *   - the third-party `respectMotionPreference` flag on `<AnimatedNumber>`.
 */
export const RESPECT_REDUCED_MOTION = false;

/** Standard animation durations, in SECONDS (Motion's transition unit). */
export const duration = {
  /** Quick micro-interactions (hover/tap). */
  fast: 0.15,
  /** Default UI transitions (fades, page/stage swaps). */
  base: 0.25,
  /** Larger / more deliberate movements. Still kept short. */
  slow: 0.4,
} as const;

/**
 * Default ease-in-out curve as a cubic-bezier tuple (Motion accepts a
 * `[number, number, number, number]` for `ease`). Standard "fast-out,
 * slow-in" material-style curve.
 */
export const easing: [number, number, number, number] = [0.4, 0, 0.2, 1];

/** A Motion spring transition preset. */
export interface SpringPreset {
  type: 'spring';
  stiffness: number;
  damping: number;
}

/**
 * Spring presets for micro-interactions and transitions.
 * - `snappy` — crisp, fast settle (cards/buttons hover/tap).
 * - `gentle` — softer, slightly slower settle (larger surfaces, reveals).
 */
export const spring: { snappy: SpringPreset; gentle: SpringPreset } = {
  snappy: { type: 'spring', stiffness: 400, damping: 30 },
  gentle: { type: 'spring', stiffness: 200, damping: 26 },
};
